import React, { useState } from 'react';
import './App.css';

// Mock Data - Users Database
const MOCK_USERS = {
  "demo": "password123",
  "alice": "pass123",
  "bob": "pass123"
};

// Mock Data - Sample Peers
const MOCK_PEERS = [
  {
    id: "1",
    name: "Sarah Chen",
    interests: ["Web Development", "Machine Learning", "Design"],
    experience_level: "Intermediate",
    bio: "Full-stack developer passionate about creating intuitive user experiences. Love collaborating on innovative projects!",
    avatar: "SC",
    goals: ["Learn new skills", "Build projects together"],
    availability: "flexible",
    communication_style: "video"
  },
  {
    id: "2",
    name: "Marcus Johnson",
    interests: ["Data Science", "Python", "Cloud Computing"],
    experience_level: "Advanced",
    bio: "Data enthusiast with 5 years of experience. Always excited to mentor and learn from others.",
    avatar: "MJ",
    goals: ["Mentor others", "Share knowledge"],
    availability: "weekdays",
    communication_style: "voice"
  },
  {
    id: "3",
    name: "Elena Rodriguez",
    interests: ["Mobile Development", "UI/UX", "React"],
    experience_level: "Intermediate",
    bio: "Mobile app developer who believes in the power of peer learning. Let's build something amazing!",
    avatar: "ER",
    goals: ["Build projects together", "Network"],
    availability: "weekends",
    communication_style: "text"
  },
  {
    id: "4",
    name: "David Kim",
    interests: ["Blockchain", "Security", "Backend Development"],
    experience_level: "Advanced",
    bio: "Security-focused backend engineer. Open to knowledge exchange and collaborative problem-solving.",
    avatar: "DK",
    goals: ["Share knowledge", "Network"],
    availability: "flexible",
    communication_style: "video"
  },
  {
    id: "5",
    name: "Priya Sharma",
    interests: ["AI", "Python", "Research"],
    experience_level: "Beginner",
    bio: "AI researcher exploring the frontiers of machine learning. Eager to connect with fellow learners!",
    avatar: "PS",
    goals: ["Learn new skills", "Get mentored"],
    availability: "weekdays",
    communication_style: "text"
  },
  {
    id: "6",
    name: "Alex Turner",
    interests: ["DevOps", "Automation", "Linux"],
    experience_level: "Intermediate",
    bio: "DevOps engineer who loves automating everything. Looking for peers to share best practices.",
    avatar: "AT",
    goals: ["Share knowledge", "Learn new skills"],
    availability: "weekends",
    communication_style: "voice"
  }
];

const App = () => {
  const [currentView, setCurrentView] = useState('login');
  const [userId, setUserId] = useState('');
  const [matchedPeers, setMatchedPeers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeer, setSelectedPeer] = useState(null);
  const [connections, setConnections] = useState([]);

  // Login Component
  const LoginView = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleLogin = (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      // Simulate API delay
      setTimeout(() => {
        // Check mock credentials
        if (MOCK_USERS[username] && MOCK_USERS[username] === password) {
          setUserId(username);
          setCurrentView('questionnaire');
        } else {
          setError('Invalid credentials');
        }
        setLoading(false);
      }, 500);
    };

    return (
      <div className="login-container">
        <div className="login-card">
          <div className="logo-section">
            <div className="logo">👥</div>
            <h1>PeerMatch</h1>
            <p>Connect with your perfect learning partner</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="input-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
              />
            </div>

            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <div className="demo-hint">
              💡 Demo credentials: username: <strong>demo</strong>, password: <strong>password123</strong>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Questionnaire Component
  const QuestionnaireView = () => {
    const [formData, setFormData] = useState({
      interests: [],
      experience_level: '',
      goals: [],
      availability: '',
      communication_style: ''
    });

    const interestOptions = [
      'Web Development', 'Mobile Development', 'Machine Learning', 
      'Data Science', 'UI/UX', 'DevOps', 'Blockchain', 
      'Cloud Computing', 'Security', 'Python', 'React', 'AI'
    ];

    const goalOptions = [
      'Learn new skills', 'Build projects together', 
      'Mentor others', 'Get mentored', 'Network', 'Share knowledge'
    ];

    const toggleSelection = (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].includes(value)
          ? prev[field].filter(item => item !== value)
          : [...prev[field], value]
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      setLoading(true);

      // Simulate API delay
      setTimeout(() => {
        // Calculate match scores based on user preferences
        const matches = calculateMatches(formData);
        setMatchedPeers(matches);
        setCurrentView('results');
        setLoading(false);
      }, 1000);
    };

    // Calculate match scores based on user's questionnaire
    const calculateMatches = (userPrefs) => {
      const scoredPeers = MOCK_PEERS.map(peer => {
        let score = 50; // Base score

        // Interest matching
        const commonInterests = peer.interests.filter(interest => 
          userPrefs.interests.includes(interest)
        ).length;
        score += commonInterests * 10;

        // Goal matching
        const commonGoals = peer.goals.filter(goal => 
          userPrefs.goals.includes(goal)
        ).length;
        score += commonGoals * 8;

        // Availability matching
        if (peer.availability === userPrefs.availability || 
            peer.availability === 'flexible' || 
            userPrefs.availability === 'flexible') {
          score += 10;
        }

        // Communication style matching
        if (peer.communication_style === userPrefs.communication_style) {
          score += 12;
        }

        // Experience level complementarity
        if (userPrefs.experience_level === 'Beginner' && peer.experience_level === 'Advanced') {
          score += 15; // Good for mentorship
        } else if (userPrefs.experience_level === peer.experience_level) {
          score += 10; // Good for peer learning
        }

        return {
          ...peer,
          match_score: Math.min(Math.round(score), 100)
        };
      });

      // Sort by score and return top 3
      return scoredPeers
        .sort((a, b) => b.match_score - a.match_score)
        .slice(0, 3);
    };

    return (
      <div className="questionnaire-container">
        <div className="questionnaire-card">
          <div className="header">
            <h2>Tell us about yourself</h2>
            <p>Help us find your perfect peer match</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <label className="section-label">What are your interests? (Select multiple)</label>
              <div className="chip-grid">
                {interestOptions.map(interest => (
                  <button
                    key={interest}
                    type="button"
                    className={`chip ${formData.interests.includes(interest) ? 'selected' : ''}`}
                    onClick={() => toggleSelection('interests', interest)}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-section">
              <label className="section-label">Experience Level</label>
              <div className="radio-grid">
                {['Beginner', 'Intermediate', 'Advanced'].map(level => (
                  <label key={level} className="radio-card">
                    <input
                      type="radio"
                      name="experience"
                      value={level}
                      checked={formData.experience_level === level}
                      onChange={(e) => setFormData({...formData, experience_level: e.target.value})}
                      required
                    />
                    <span>{level}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-section">
              <label className="section-label">What are your goals? (Select multiple)</label>
              <div className="chip-grid">
                {goalOptions.map(goal => (
                  <button
                    key={goal}
                    type="button"
                    className={`chip ${formData.goals.includes(goal) ? 'selected' : ''}`}
                    onClick={() => toggleSelection('goals', goal)}
                  >
                    {goal}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-section">
              <label className="section-label">Availability</label>
              <select
                value={formData.availability}
                onChange={(e) => setFormData({...formData, availability: e.target.value})}
                required
              >
                <option value="">Select availability</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekends">Weekends</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>

            <div className="form-section">
              <label className="section-label">Communication Style</label>
              <select
                value={formData.communication_style}
                onChange={(e) => setFormData({...formData, communication_style: e.target.value})}
                required
              >
                <option value="">Select style</option>
                <option value="text">Text/Chat</option>
                <option value="voice">Voice Calls</option>
                <option value="video">Video Calls</option>
                <option value="in-person">In-Person</option>
              </select>
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Finding matches...' : 'Find My Peers'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // Results Component
  const ResultsView = () => {
    const handleConnect = (peer) => {
      // Check if already connected
      const alreadyConnected = connections.find(c => c.id === peer.id);
      
      if (!alreadyConnected) {
        setConnections(prev => [...prev, peer]);
      }
      
      setSelectedPeer(peer);
      setCurrentView('chat');
    };

    return (
      <div className="results-container">
        <div className="results-header">
          <h2>Your Perfect Matches 🎉</h2>
          <p>We found {matchedPeers.length} peers that match your profile</p>
        </div>

        <div className="peers-grid">
          {matchedPeers.map(peer => (
            <div key={peer.id} className="peer-card">
              <div className="peer-header">
                <div className="avatar">{peer.avatar}</div>
                <div className="peer-info">
                  <h3>{peer.name}</h3>
                  <span className="experience-badge">{peer.experience_level}</span>
                </div>
                <div className="match-score">
                  <div className="score-circle">{peer.match_score}%</div>
                  <span>Match</span>
                </div>
              </div>

              <p className="peer-bio">{peer.bio}</p>

              <div className="interests-section">
                <strong>Interests:</strong>
                <div className="interest-tags">
                  {peer.interests.map(interest => (
                    <span key={interest} className="tag">{interest}</span>
                  ))}
                </div>
              </div>

              <button 
                className="btn-connect"
                onClick={() => handleConnect(peer)}
              >
                Connect & Chat
              </button>
            </div>
          ))}
        </div>

        <button 
          className="btn-secondary"
          onClick={() => setCurrentView('questionnaire')}
        >
          Retake Questionnaire
        </button>
      </div>
    );
  };

  // Chat Component
  const ChatView = () => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [chatLoading, setChatLoading] = useState(false);

    const sendMessage = (e) => {
      e.preventDefault();
      if (!newMessage.trim()) return;

      const userMessage = {
        id: Date.now(),
        sender: userId,
        text: newMessage,
        timestamp: new Date().toLocaleTimeString()
      };

      setMessages(prev => [...prev, userMessage]);
      const messageText = newMessage;
      setNewMessage('');
      setChatLoading(true);

      // Simulate typing delay and generate response
      setTimeout(() => {
        const peerMessage = {
          id: Date.now() + 1,
          sender: selectedPeer.id,
          text: generatePeerResponse(selectedPeer, messageText),
          timestamp: new Date().toLocaleTimeString()
        };
        setMessages(prev => [...prev, peerMessage]);
        setChatLoading(false);
      }, 1000 + Math.random() * 1500); // Random delay between 1-2.5 seconds
    };

    // Generate contextual responses based on peer's profile
    const generatePeerResponse = (peer, userMessage) => {
      const messageLower = userMessage.toLowerCase();
      
      // Greeting responses
      if (messageLower.match(/\b(hi|hello|hey|greetings)\b/)) {
        const greetings = [
          `Hey! Great to connect with you! I'm really into ${peer.interests.slice(0, 2).join(' and ')}. What brings you here?`,
          `Hello! Nice to meet you! I'm passionate about ${peer.interests[0]}. How can I help?`,
          `Hi there! Excited to chat! I've been working on some ${peer.interests[0]} projects lately.`
        ];
        return greetings[Math.floor(Math.random() * greetings.length)];
      }
      
      // Help/question responses
      if (messageLower.match(/\b(help|question|issue|problem|stuck)\b/)) {
        const helpResponses = [
          `I'd be happy to help! As a ${peer.experience_level.toLowerCase()} in ${peer.interests[0]}, I've dealt with similar challenges. Can you share more details?`,
          "That's what I'm here for! Let's break down the problem together. What specific area are you struggling with?",
          `No worries, we all face challenges! With my experience in ${peer.interests.slice(0, 2).join(' and ')}, I might have some insights. Tell me more!`,
          "I love problem-solving! Let's tackle this together. Can you describe what you've tried so far?"
        ];
        return helpResponses[Math.floor(Math.random() * helpResponses.length)];
      }
      
      // Learning/skill responses
      if (messageLower.match(/\b(learn|study|practice|skill|tutorial|course)\b/)) {
        const learningResponses = [
          `Learning is my passion! For ${peer.interests[0]}, I'd recommend starting with hands-on projects. What's your current level?`,
          `Great question! I've found the best way to learn ${peer.interests[0]} is through consistent practice and building real projects.`,
          "I'm always learning too! Let's explore this together. What specific aspect are you interested in?",
          `As someone at ${peer.experience_level.toLowerCase()} level, I can share resources that really helped me. What's your learning style?`
        ];
        return learningResponses[Math.floor(Math.random() * learningResponses.length)];
      }
      
      // Project collaboration
      if (messageLower.match(/\b(project|build|collaborate|work together|team)\b/)) {
        const projectResponses = [
          `I'd love to collaborate! I'm currently exploring ${peer.interests[0]} and ${peer.interests[1] || 'new technologies'}. What did you have in mind?`,
          "Building projects together is the best way to learn! What kind of project are you thinking about?",
          `Count me in! With my background in ${peer.interests.slice(0, 2).join(' and ')}, we could create something amazing. Share your ideas!`,
          `Collaboration sounds great! I'm available ${peer.availability}. What's your vision for this project?`
        ];
        return projectResponses[Math.floor(Math.random() * projectResponses.length)];
      }
      
      // Check for specific tech mentions
      for (const interest of peer.interests) {
        if (messageLower.includes(interest.toLowerCase())) {
          const techResponses = [
            `Oh, you're interested in ${interest} too! That's one of my main areas. What aspect would you like to discuss?`,
            `Great topic! I've been working with ${interest} for a while now. What would you like to know?`,
            `${interest} is fascinating! I'm currently ${peer.experience_level.toLowerCase()} level with it. How can I assist you?`,
            `Perfect! ${interest} is something I'm really passionate about. Let's dive deeper!`
          ];
          return techResponses[Math.floor(Math.random() * techResponses.length)];
        }
      }
      
      // Default responses
      const defaultResponses = [
        `That's interesting! As someone who loves ${peer.interests[0]}, I'm always excited to explore new ideas. Tell me more!`,
        "I appreciate you reaching out! Let's work through this together. Can you provide more context?",
        `Thanks for sharing! With my ${peer.experience_level.toLowerCase()} experience, I think I can help. What's the next step you're considering?`,
        "I'm here to help! Let's discuss this further. What specific outcome are you looking for?",
        `Interesting perspective! I've encountered similar situations while working with ${peer.interests[0]}. Want to brainstorm solutions?`,
        `Great question! I'm available ${peer.availability} to dive deeper into this. What's your timeline?`,
        "I see what you mean! Let's approach this systematically. What have you discovered so far?"
      ];
      
      return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
    };

    return (
      <div className="chat-container">
        <div className="chat-header">
          <button 
            className="back-button"
            onClick={() => setCurrentView('results')}
          >
            ← Back to Matches
          </button>
          <div className="chat-peer-info">
            <div className="avatar-small">{selectedPeer.avatar}</div>
            <div>
              <h3>{selectedPeer.name}</h3>
              <span className="status">● Online</span>
            </div>
          </div>
          <button 
            className="connections-button"
            onClick={() => setCurrentView('connections')}
          >
            My Connections ({connections.length})
          </button>
        </div>

        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="chat-welcome">
              <h3>👋 Start a conversation with {selectedPeer.name}!</h3>
              <p>Share your learning goals, ask questions, or discuss project ideas.</p>
            </div>
          ) : (
            messages.map(msg => (
              <div 
                key={msg.id} 
                className={`message ${msg.sender === userId ? 'message-sent' : 'message-received'}`}
              >
                <div className="message-content">
                  <p>{msg.text}</p>
                  <span className="message-time">{msg.timestamp}</span>
                </div>
              </div>
            ))
          )}
          {chatLoading && (
            <div className="message message-received">
              <div className="message-content typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}
        </div>

        <form className="chat-input-container" onSubmit={sendMessage}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="chat-input"
            disabled={chatLoading}
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={chatLoading || !newMessage.trim()}
          >
            Send 📤
          </button>
        </form>
      </div>
    );
  };

  // Connections Component
  const ConnectionsView = () => {
    return (
      <div className="connections-container">
        <div className="connections-header">
          <h2>My Connections</h2>
          <button 
            className="btn-secondary"
            onClick={() => setCurrentView('results')}
          >
            Find More Matches
          </button>
        </div>

        {connections.length === 0 ? (
          <div className="empty-state">
            <h3>No connections yet</h3>
            <p>Connect with matched peers to start chatting!</p>
            <button 
              className="btn-primary"
              onClick={() => setCurrentView('results')}
            >
              View Matches
            </button>
          </div>
        ) : (
          <div className="connections-list">
            {connections.map(peer => (
              <div key={peer.id} className="connection-card">
                <div className="avatar">{peer.avatar}</div>
                <div className="connection-info">
                  <h3>{peer.name}</h3>
                  <p>{peer.experience_level}</p>
                  <div className="match-score-small">{peer.match_score}% Match</div>
                </div>
                <button 
                  className="btn-primary"
                  onClick={() => {
                    setSelectedPeer(peer);
                    setCurrentView('chat');
                  }}
                >
                  Open Chat 💬
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app">
      {currentView === 'login' && <LoginView />}
      {currentView === 'questionnaire' && <QuestionnaireView />}
      {currentView === 'results' && <ResultsView />}
      {currentView === 'chat' && <ChatView />}
      {currentView === 'connections' && <ConnectionsView />}
    </div>
  );
};

export default App;